import cn from 'classnames';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { Icon, IconButton } from 'components';
import { ButtonV3, InputV2 } from 'components/ComponentV2';
import ImageUpload from 'components/ImageUpload';
import Modal from 'components/Modal';
import { Row } from 'components/layout';

import MultiSelect from 'components/MultiSelect';
import { Tooltip } from 'components/Tooltip/Tooltip.component';
import { TextArea, Toggle } from 'components/form';
import { useResponsive } from 'hooks/useResponsive';
import useScrollOffset from 'hooks/useScrollOffset';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { useCommonList } from 'lib/contexts/common-list-context';
import { useAuth } from 'lib/providers/AuthProvider';
import { REGEX } from 'lib/utils/regex';
import { convertToBase64 } from 'utils/format';

import styles from '../ProfileModal.common.module.scss';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CandidateInfo {
  languages: string[];
  firstName: string | undefined;
  lastName: string | undefined;
  bio: string | undefined;
  avatarUrl: string | undefined;
  location: string | undefined;
  showAvatarInCv: boolean | undefined;
  showBioInCv: boolean | undefined;
  linkedinUrl: string | undefined;
  phoneNumber: string | undefined;
  email: string | undefined;
}

const initialCandidateInfo: CandidateInfo = {
  languages: [],
  firstName: undefined,
  lastName: undefined,
  bio: undefined,
  avatarUrl: undefined,
  location: undefined,
  showAvatarInCv: undefined,
  showBioInCv: undefined,
  linkedinUrl: undefined,
  phoneNumber: undefined,
  email: undefined,
};

export const AddBasicInfoModal = memo(
  ({ isOpen, onClose }: ProfileModalProps) => {
    const [t] = useTranslation('candidate.profile.modal');

    const screens = useResponsive();

    const headerRef = useRef<HTMLDivElement>(null);
    const languageDropdownRef = useRef<HTMLDivElement>(null);
    const { getScrollOffset } = useScrollOffset();
    const { languageOptions: languageTag } = useCommonList();

    const { updateCandidate } = useCandidateProfile();

    const { candidate } = useAuth();

    const [candidateInfo, setCandidateInfo] =
      useState<CandidateInfo>(initialCandidateInfo);

    useEffect(() => {
      if (candidate) {
        const {
          first_name,
          last_name,
          bio,
          avatar_url,
          languages,
          location,
          email,
          linkedin_url,
          phone_number,
          show_avatar_in_cv,
          show_bio_in_cv,
        } = candidate;

        setCandidateInfo({
          ...candidateInfo,
          firstName: first_name,
          lastName: last_name,
          bio: bio,
          avatarUrl: avatar_url,
          location: location,
          languages: languages?.data?.map((item) => item.id) || [],
          showAvatarInCv: show_avatar_in_cv || false,
          showBioInCv: show_bio_in_cv || false,
          linkedinUrl: linkedin_url,
          phoneNumber: phone_number,
          email: email,
        });
      }
    }, []);

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      const keyCode = event.key;

      if (!REGEX.PHONE_NUMBER.test(keyCode)) {
        event.preventDefault();
      }
    };

    const onChangeLogo = async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event !== null && event.target.files?.length) {
        const image = event.target.files[0];
        const fileInBase64String = await convertToBase64(image);
        setCandidateInfo((prevInfo) => ({
          ...prevInfo,
          avatarUrl: URL.createObjectURL(image),
        }));

        updateCandidate({ avatar: { file: fileInBase64String } });
      }
    };

    const onRemove = (
      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
      if (event !== null) {
        setCandidateInfo((prevInfo) => ({
          ...prevInfo,
          avatarUrl: undefined,
        }));

        updateCandidate({ remove_avatar: true }, false, true);
      }
    };

    const changeShowBioInCV = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event) {
        setCandidateInfo((prevInfo) => ({
          ...prevInfo,
          showBioInCv: event.target.checked,
        }));
      }
    };

    const changeShowAvatarInCV = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (event) {
        setCandidateInfo((prevInfo) => ({
          ...prevInfo,
          showAvatarInCv: event.target.checked,
        }));
      }
    };

    const onLanguagesSelect = (values: Array<string>) => {
      setCandidateInfo((prevInfo) => ({
        ...prevInfo,
        languages: values,
      }));
    };

    const updateCandidateData = () => {
      const isLinkedinValid =
        (candidateInfo.linkedinUrl &&
          REGEX.URL.test(candidateInfo.linkedinUrl)) ||
        !candidateInfo.linkedinUrl
          ? true
          : false;

      const isPhoneValid =
        (candidateInfo.phoneNumber &&
          REGEX.PHONE_NUMBER.test(candidateInfo.phoneNumber)) ||
        !candidateInfo.phoneNumber
          ? true
          : false;

      if (isLinkedinValid && isPhoneValid) {
        updateCandidate({
          show_avatar_in_cv: candidateInfo.showAvatarInCv,
          first_name: candidateInfo.firstName,
          last_name: candidateInfo.lastName,
          bio: candidateInfo.bio,
          // hide till Bio toggle is hidden
          // show_bio_in_cv: candidateInfo.showBioInCv,
          language_ids: candidateInfo.languages,
          location: candidateInfo.location,
          linkedin_url: candidateInfo.linkedinUrl,
          phone_number: candidateInfo.phoneNumber,
        });
        onClose();
      }
    };

    const selectedLanguageText = useMemo(() => {
      const labelsList = languageTag
        ?.filter((item) => {
          if (
            candidateInfo.languages &&
            candidateInfo.languages.includes(item.id)
          )
            return item.label;
        })
        .map((item) => item.label);
      return labelsList?.join(', ') || t('basic-information.language.select');
    }, [candidateInfo.languages, languageTag]);

    const viewportRef = useRef(document.documentElement);
    const scrollToDropdown = (dropdownRef: React.RefObject<HTMLElement>) => {
      const topSpace = 20;
      const headerHeight = 62;
      const adjustment = topSpace + headerHeight;
      const offset = getScrollOffset(
        dropdownRef,
        viewportRef,
        headerRef,
        adjustment
      );
      window.scrollTo({ top: offset, behavior: 'smooth' });
    };

    const scrollToDropdownLanguage = () => {
      scrollToDropdown(languageDropdownRef);
    };

    return (
      <Modal
        isOpen={isOpen}
        onRequestClose={onClose}
        contentClassName={cn(styles.ProfileModal, styles.basicInformationModal)}
      >
        <div
          className={cn(
            styles.ProfileModalHeader,
            styles.basicInformationModal
          )}
        >
          <p>{t('basic-information.title')}</p>
          <IconButton
            onClick={onClose}
            className={styles.ProfileModalHeaderClose}
            iconName="close"
            size="small"
          />
          <IconButton
            onClick={onClose}
            className={styles.ProfileModalHeaderCloseMobile}
            iconName="arrow-button-left"
            size="small"
          />
        </div>
        <div className={styles.ProfileModalContent}>
          <Row
            align="center"
            justify="space-between"
            className={cn(styles.inputRow)}
          >
            <ImageUpload
              key={candidateInfo.avatarUrl}
              image={candidateInfo.avatarUrl || ''}
              alt={t('basic-information.profile-photo.alt-text')}
              name={candidateInfo.firstName || candidateInfo.email}
              onChange={onChangeLogo}
              label={
                candidateInfo.avatarUrl
                  ? screens.sm
                    ? t('basic-information.profile-photo.change')
                    : t('basic-information.profile-photo.change-photo')
                  : t('basic-information.profile-photo.add-photo')
              }
              buttonIconName={candidateInfo.avatarUrl ? undefined : 'plus'}
              imageSrc={candidateInfo.avatarUrl ? undefined : 'aws'}
              className={styles.photoUpload}
              onRemove={onRemove}
              showRemovePhotoBtn={false}
            />

            <div className={styles.toggleSection}>
              <Toggle
                checked={
                  candidateInfo.showAvatarInCv && candidateInfo.avatarUrl
                    ? true
                    : false
                }
                onChange={changeShowAvatarInCV}
                className={styles.toggle}
                disabled={!candidateInfo.avatarUrl}
                toggleSize={'small'}
              />
              <label>{t('basic-information.show-in-cv')}</label>
            </div>
          </Row>
          <Row className={styles.inputRow}>
            <InputV2
              maxLength={120}
              label={t('basic-information.first-name.label')}
              placeholder={t('basic-information.first-name.placeholder')}
              value={candidateInfo.firstName}
              onChange={(event) => {
                setCandidateInfo((prevInfo) => ({
                  ...prevInfo,
                  firstName: event.target.value,
                }));
              }}
              className={cn(styles.input, styles.width50)}
            />

            <InputV2
              maxLength={120}
              label={t('basic-information.last-name.label')}
              placeholder={t('basic-information.last-name.placeholder')}
              value={candidateInfo.lastName}
              onChange={(event) => {
                setCandidateInfo((prevInfo) => ({
                  ...prevInfo,
                  lastName: event.target.value,
                }));
              }}
              className={cn(styles.input, styles.width50)}
            />
          </Row>
          <Row className={styles.inputRow}>
            <TextArea
              maxLength={160}
              label={t('basic-information.bio.label')}
              placeholder={t('basic-information.bio.placeholder')}
              showOptionalLabel={true}
              rows={screens.sm ? 2 : 1}
              value={candidateInfo.bio || ''}
              onChange={(event) => {
                setCandidateInfo((prevInfo) => ({
                  ...prevInfo,
                  bio: event.target.value,
                }));
              }}
              isValid={true}
              className={styles.input}
              textAreaClassName={cn(styles.textArea, styles.bioTextBox)}
              infoToolTip={
                <Tooltip
                  content={t('basic-information.bio.tooltip')}
                  position="top"
                  color="dark"
                  className={styles.tooltipContainer}
                >
                  <Icon
                    iconName="icon_info"
                    className={styles.tooltipContainerIcon}
                    size="auto"
                  />
                </Tooltip>
              }
            />
            {/* Hidden till CV is updated with Bio section */}
            <div className={styles.toggleSection} style={{ display: 'none' }}>
              <Toggle
                checked={candidateInfo.showBioInCv ? true : false}
                onChange={changeShowBioInCV}
                className={styles.toggle}
                toggleSize={'small'}
              />
              <label>{t('basic-information.show-in-cv')}</label>
            </div>
          </Row>
          <Row className={cn(styles.inputRow, styles['language-row'])}>
            {/* language input field */}
            <div className={styles.InputContainer}>
              <div className={styles.Multiselect}>
                <div className={styles.MultiselectLabel}>
                  <span>{t('basic-information.language.label')}</span>
                </div>
                <span className={styles.optionalLabel}>
                  {t('basic-information.language.optional')}
                </span>
                <Tooltip
                  content={t('basic-information.language.tooltip')}
                  position="top"
                  xAlign="center"
                  color="dark"
                  className={cn(
                    styles.tooltipContainer,
                    styles.tooltipContainerLanguage
                  )}
                  containerClassName={styles.tooltipContainerContent}
                >
                  <Icon
                    iconName="icon_info"
                    className={styles.tooltipContainerIcon}
                    size="auto"
                  />
                </Tooltip>
              </div>
              <div ref={languageDropdownRef}>
                <MultiSelect
                  title={
                    candidateInfo.languages &&
                    candidateInfo.languages.length === 0
                      ? t('basic-information.language.select')
                      : selectedLanguageText
                  }
                  placeholder={t('basic-information.language.placeholder')}
                  options={languageTag}
                  values={candidateInfo.languages || []}
                  onChange={onLanguagesSelect}
                  className={cn(styles.dropdown)}
                  dropdownClassName={cn(
                    styles.multiDropdown,
                    styles.multiDropdownBottom
                  )}
                  checkBoxClassName={styles.checkbox}
                  showCount={false}
                  isSearchable
                  fullWidth
                  iconClassName="icon_arrow-down"
                  buttonTitleClassName={styles.multiDropdownTrigger}
                  scrollToDropdown={scrollToDropdownLanguage}
                />
              </div>
            </div>
          </Row>
          <Row className={styles.inputRow}>
            <InputV2
              label={t('common.location.label')}
              placeholder={t('common.location.placeholder')}
              showOptionalLabel={true}
              value={candidateInfo.location}
              maxLength={160}
              onChange={(event) => {
                setCandidateInfo((prevInfo) => ({
                  ...prevInfo,
                  location: event.target.value,
                }));
              }}
              className={styles.input}
              infoToolTip={
                <Tooltip
                  content={t('common.location.tooltip')}
                  position="top"
                  xAlign="center"
                  color="dark"
                  className={cn(
                    styles.tooltipContainer,
                    styles.tooltipContainerLanguage
                  )}
                  containerClassName={styles.tooltipContainerContent}
                >
                  <Icon
                    iconName="icon_info"
                    className={styles.tooltipContainerIcon}
                    size="auto"
                  />
                </Tooltip>
              }
            />
          </Row>
          <div className={styles.contactInfoTitle}>Contact information</div>
          <Row className={styles.inputRow}>
            <InputV2
              label={t('basic-information.email.label')}
              value={candidateInfo.email}
              disabled={true}
              className={styles.input}
            />
          </Row>
          <Row className={styles.inputRow}>
            <InputV2
              label={t('basic-information.linkedin.label')}
              placeholder={t('basic-information.linkedin.placeholder')}
              showOptionalLabel={true}
              maxLength={160}
              value={candidateInfo.linkedinUrl}
              onChange={(event) => {
                setCandidateInfo((prevInfo) => ({
                  ...prevInfo,
                  linkedinUrl: event.target.value,
                }));
              }}
              isInvalid={
                candidateInfo.linkedinUrl &&
                !REGEX.URL.test(candidateInfo.linkedinUrl)
                  ? true
                  : false
              }
              className={styles.input}
            />
          </Row>
          <Row className={styles.inputRow}>
            <InputV2
              label={t('basic-information.phone-number.label')}
              placeholder={t('basic-information.phone-number.placeholder')}
              type="tel"
              showOptionalLabel={true}
              value={candidateInfo.phoneNumber}
              maxLength={16}
              onKeyPress={handleKeyPress}
              onChange={(event) => {
                setCandidateInfo((prevInfo) => ({
                  ...prevInfo,
                  phoneNumber: event.target.value,
                }));
              }}
              isInvalid={
                candidateInfo.phoneNumber &&
                !REGEX.PHONE_NUMBER.test(candidateInfo.phoneNumber)
                  ? true
                  : false
              }
              className={styles.input}
            />
          </Row>
        </div>
        <div className={cn(styles.ProfileModalAction, styles.singleCta)}>
          <ButtonV3
            onClick={updateCandidateData}
            className={styles.button}
            disabled={false}
            isLoading={false}
          >
            {t('common.done')}
          </ButtonV3>
        </div>
      </Modal>
    );
  }
);

AddBasicInfoModal.displayName = 'AddBasicInfoModal';
